import clsx from "clsx";
import { CheckCircle2, Info, TriangleAlert, X } from "lucide-react";
import { useEffect, useRef } from "react";

import { type Notice, useUi } from "../state/ui";
import { attentionSelector, designateSoon } from "./attention";
import { motion, useLastDefined, usePresence } from "./presence";
import { Button, IconButton } from "./ui";
import { t } from "../i18n/i18n";
import { ReportAnomalyLink } from "../feedback/reportLink";

const DURATION_MS = 10_000;
/** Cible de l'action attendue d'un avis (`Notice.action.attention`). */
const NOTICE_ACTION = "notice-action";

/** Icône et couleur de chaque ton d'avis : erreur en rouge `danger`, avertissement en orange, confirmation en
 *  vert, information en bleu d'action. */
const TONE: Record<Notice["tone"], { Icon: typeof Info; className: string }> = {
  error: { Icon: TriangleAlert, className: "text-danger" },
  warn: { Icon: TriangleAlert, className: "text-warn" },
  ok: { Icon: CheckCircle2, className: "text-ok" },
  info: { Icon: Info, className: "text-accent" },
};

/** Avis flottant au-dessus de la barre basse de la carte (R10) : résultat d'une action (`notify`), avec une
 *  action facultative (« Annuler »). Disparaît au bout de 10 s, comme l'aperçu des rôles de l'iPad. Il monte
 *  depuis la barre basse en fondu et redescend de même ; un nouvel avis remplace le précédent en rejouant
 *  l'entrée. Titre en 12 px semi-gras, détail en 11,5 px. Plan : z-30 (posé par son conteneur). Avis d'erreur :
 *  lien « Signaler cette anomalie » sous le texte (registre F39), qui reprend le titre et le détail. */
export function NoticeToast() {
  const current = useUi((s) => s.notice);
  const notify = useUi((s) => s.notify);
  const { mounted, closing } = usePresence(Boolean(current));
  const notice = useLastDefined(current);
  const text = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!current) return;
    const timer = window.setTimeout(() => notify(null), DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [current, notify]);

  // Action attendue de l'avis (réancrer les positions…) : désignée une fois l'avis apparu (R12).
  useEffect(() => {
    if (!current?.action?.attention) return;
    return designateSoon(attentionSelector(NOTICE_ACTION), "ring", 1200);
  }, [current]);

  if (!mounted || !notice) return null;
  const { Icon, className } = TONE[notice.tone] ?? TONE.info;

  return (
    <div
      key={notice.id}
      role={notice.tone === "error" ? "alert" : "status"}
      aria-live={notice.tone === "error" ? "assertive" : "polite"}
      className={clsx(
        "pointer-events-auto flex max-w-[560px] items-start gap-3 rounded-xl border border-line bg-surface py-2.5 pr-2 pl-3.5 shadow-float",
        motion("bottom", closing),
      )}
    >
      <Icon size={16} className={clsx("mt-0.5 shrink-0", className)} />
      <div className="min-w-0 flex-1 py-px">
        <div ref={text}>
          <div className="text-ui leading-snug font-semibold break-words text-ink">{notice.text}</div>
          {notice.detail && <div className="mt-0.5 text-meta leading-snug break-words text-ink-muted">{notice.detail}</div>}
        </div>
        {notice.tone === "error" && <ReportAnomalyLink source={text} className="mt-1" />}
      </div>
      {[notice.action, notice.secondary].map(
        (action, i) =>
          action && (
            // Lien d'action avec la zone de frappe de l'ancien bouton (px-2 py-1, environ 26 px de haut au doigt).
            <Button
              key={i}
              data-attention={!i && notice.action?.attention ? NOTICE_ACTION : undefined}
              variant="link"
              className={clsx("-my-0.5 shrink-0 py-1", i ? "pr-2 pl-1" : "px-2")}
              onClick={() => {
                action.run();
                notify(null);
              }}
            >
              {action.label}
            </Button>
          ),
      )}
      <IconButton size="sm" onClick={() => notify(null)} title={t("Fermer")} aria-label={t("Fermer")} className="-my-1">
        <X size={14} />
      </IconButton>
    </div>
  );
}
